import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Check, X, History, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { HoldingAudit, HoldingWithInstrumentAndAccount } from "@/types";
import { AuditSheet } from "@/components/AuditSheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAtomValue } from "jotai";
import { SelectedReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface HoldingsTableProps {
  isLoading: boolean;
  holdings: HoldingWithInstrumentAndAccount[];
  onViewHolding: (holding: HoldingWithInstrumentAndAccount) => void;
  onConfirmHolding: (
    holding: HoldingWithInstrumentAndAccount,
    isConfirmed: boolean
  ) => void;
  onDeleteHolding: (holding: HoldingWithInstrumentAndAccount) => void;
}

export const HoldingsTable = ({
  isLoading,
  holdings,
  onViewHolding,
  onConfirmHolding,
  onDeleteHolding,
}: HoldingsTableProps) => {
  const [auditOpen, setAuditOpen] = useState(false);
  const [selectedHolding, setSelectedHolding] =
    useState<HoldingWithInstrumentAndAccount | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [holdingToDelete, setHoldingToDelete] =
    useState<HoldingWithInstrumentAndAccount | null>(null);

  // Get current reporting period to check if it's closed
  const selectedReportingPeriod = useAtomValue(SelectedReportingPeriodAtom);
  const isPeriodClosed = !selectedReportingPeriod?.isOpen;

  const handleAuditClick = (holding: HoldingWithInstrumentAndAccount) => {
    setSelectedHolding(holding);
    setAuditOpen(true);
  };

  const handleDeleteClick = (holding: HoldingWithInstrumentAndAccount) => {
    if (isPeriodClosed) return;
    setHoldingToDelete(holding);
    setDeleteDialogOpen(true);
  };

  const handleConfirmClick = (holding: HoldingWithInstrumentAndAccount) => {
    if (isPeriodClosed) return;
    onConfirmHolding(holding, !holding.isConfirmed);
  };

  const handleDeleteConfirm = () => {
    if (holdingToDelete) {
      onDeleteHolding(holdingToDelete);
      setDeleteDialogOpen(false);
      setHoldingToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setHoldingToDelete(null);
  };

  const renderHoldingData = (data: HoldingAudit) => (
    <div className="space-y-2 text-sm">
      <div>
        <strong>Key:</strong> {data.holdingKey}
      </div>
      <div>
        <strong>Instrument:</strong> {data.instrumentName}
      </div>
      <div>
        <strong>Quantity:</strong> {data.quantity}
      </div>
      <div>
        <strong>Amount:</strong> {data.baseAmount}
      </div>
      <div>
        <strong>Currency:</strong> {data.currency}
      </div>
      <div>
        <strong>Account:</strong> {data.accountName}
      </div>
      <div>
        <strong>Confirmed:</strong> {data.isConfirmed ? "Yes" : "No"}
      </div>
    </div>
  );
  // Helper function to format currency amounts
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  };

  // Helper function to format percentage
  const formatPercentage = (percentage: number | undefined) => {
    if (percentage === undefined) return "-";
    return `${percentage.toFixed(2)}%`;
  };

  // Helper function to get badge variant based on asset class
  const getAssetClassBadgeVariant = (assetClass: string) => {
    switch (assetClass.toLowerCase()) {
      case "equity":
        return "default";
      case "fixed income":
        return "secondary";
      case "cash":
        return "outline";
      case "alternative":
        return "destructive";
      default:
        return "secondary";
    }
  };

  // Helper function to get confirmation badge variant
  const getConfirmationBadgeVariant = (isConfirmed: boolean) => {
    return isConfirmed ? "success" : "destructive";
  };

  return (
    <div className="max-h-[80vh] overflow-auto">
      <Table>
        <TableHeader className="sticky top-0 bg-background z-10">
          <TableRow>
            <TableHead>Key</TableHead>
            <TableHead>Instrument</TableHead>
            <TableHead className="text-right">Quantity</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Currency</TableHead>
            <TableHead>Account</TableHead>
            <TableHead>Confirmed</TableHead>
            <TableHead className="w-32 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={10} className="text-center py-4">
                Loading holdings...
              </TableCell>
            </TableRow>
          ) : holdings.length > 0 ? (
            holdings.map((holding) => (
              <TableRow key={holding.holdingId}>
                <TableCell>{holding.holdingKey}</TableCell>
                <TableCell>{holding.instrument?.name}</TableCell>
                <TableCell className="text-right">{holding.quantity}</TableCell>
                <TableCell className="text-right">
                  {holding.baseAmount}
                </TableCell>
                <TableCell>{holding.currency}</TableCell>
                <TableCell>{holding.account?.name}</TableCell>
                <TableCell>
                  <Badge
                    variant={getConfirmationBadgeVariant(holding.isConfirmed)}
                  >
                    {holding.isConfirmed ? "Confirmed" : "Unconfirmed"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex space-x-1 justify-end">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleAuditClick(holding)}
                      title="View Audit History"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant={
                              holding.isConfirmed ? "outline" : "default"
                            }
                            size="icon"
                            onClick={() => handleConfirmClick(holding)}
                            disabled={isPeriodClosed}
                            title={
                              isPeriodClosed
                                ? "Cannot modify confirmation when reporting period is closed"
                                : holding.isConfirmed
                                ? "Unconfirm"
                                : "Confirm"
                            }
                          >
                            {holding.isConfirmed ? (
                              <X className="h-4 w-4" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        {isPeriodClosed && (
                          <TooltipContent>
                            <p>
                              Cannot modify confirmation when reporting period
                              is closed
                            </p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="destructive"
                            size="icon"
                            onClick={() => handleDeleteClick(holding)}
                            disabled={isPeriodClosed}
                            title={
                              isPeriodClosed
                                ? "Cannot delete holding when reporting period is closed"
                                : "Delete Holding"
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        {isPeriodClosed && (
                          <TooltipContent>
                            <p>
                              Cannot delete holding when reporting period is
                              closed
                            </p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-4">
                No holdings found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {selectedHolding && (
        <AuditSheet
          open={auditOpen}
          onOpenChange={setAuditOpen}
          title="Holding"
          entityId={selectedHolding.holdingId}
          entityType="holding"
          renderCurrentData={renderHoldingData}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Holding</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this holding? This action cannot
              be undone.
              {holdingToDelete && (
                <div className="mt-2 p-2 bg-muted rounded">
                  <strong>Holding Key:</strong> {holdingToDelete.holdingKey}
                  <br />
                  <strong>Instrument:</strong>{" "}
                  {holdingToDelete.instrument?.name}
                  <br />
                  <strong>Amount:</strong> {holdingToDelete.baseAmount}{" "}
                  {holdingToDelete.currency}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="text-white bg-destructive "
              onClick={handleDeleteConfirm}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
